import { defineStore } from 'pinia';
import UsersService from '@/Services/UsersService';
import { apiClient } from '@/Clients/ApiClient.js';
import InitialState from '@/InitialState';
import Config from '@/Config';
import WebStorage from '@/Utils/WebStorage';

const {
    avatarUrl,
    avatarThumbUrl,
    isAuthenticated,
    isImpersonated,
    user: {
        creatorPermissions,
        email,
        fullName,
        globalPermissions,
        nickname,
        projectPermissions,
        quirkFlags,
        roleFlags,
        systemPermissions,
        userID,
        restrictions,
        hasPublicProfile,
        hasPushNotificationsEnabled,
        privacySettingFlags,
        privacySettingsUpdatedAt,
        hasRetailerAccess,
    },
} = InitialState.userContext || {};

const followedUsersIDs = InitialState.followedUsersIDs;

export const useUserContextStore = defineStore('userContext', {
    state: () => ({
        userContext: {
            avatarUrl,
            avatarThumbUrl,
            isAuthenticated,
            isImpersonated,
            user: {
                creatorPermissions,
                email,
                followedUsersIDs,
                fullName,
                globalPermissions,
                nickname,
                projectPermissions,
                quirkFlags,
                roleFlags,
                systemPermissions,
                userID,
                restrictions,
                hasPublicProfile,
                hasPushNotificationsEnabled,
                privacySettingFlags,
                privacySettingsUpdatedAt,
                hasRetailerAccess,
            },
        },
    }),
    actions: {
        async reloadUserContext() {
            const userService = new UsersService(apiClient);
            const response = await userService.getUserContext();
            this.userContext = response.data;
            return response;
        },
        async enableSocialFeatures({ nickname, isPublic }) {
            const userService = new UsersService(apiClient);
            const response = await userService.enableSocialFeatures({ nickname, isPublic });

            if (response.data.success) {
                return await this.reloadUserContext();
            }
            else {
                throw new Error(response.data.message);
            }
        },
        async revertImpersonation() {
            const userService = new UsersService(apiClient);
            const response = await userService.revertImpersonation();
            if (response.data.success) {
                window.location = response.data.data.returnUrl;
            }
            else {
                throw new Error(response.data.message);
            }
        },
        logout(displayMode, returnUrl = null) {
            if (displayMode?.isPWA) {
                const pushNotificationModalDisplayedStorage = new WebStorage('pushNotificationModalDisplayedStorage');
                const localStorage = pushNotificationModalDisplayedStorage.get() || [];

                const localStorageItem = localStorage.find(
                    ({ userId }) => userId === this.userId);

                if (localStorageItem) {
                    localStorageItem.isPushNotificationModalDisplayed = false;
                    pushNotificationModalDisplayedStorage.set(localStorage);
                }
            }

            let url = Config.navigation.logoutUrl;
            if (returnUrl && url) {
                const parsed = new URL(url, window.location.origin);
                parsed.searchParams.set('returnUrl', returnUrl);
                url = parsed.toString();
            }

            window.location.href = url;
        },
    },
    getters: {
        isAuthenticated() {
            return this.userContext?.isAuthenticated;
        },
        isUserBanned() {
            return this.userContext?.user?.restrictions?.commentsRestriction;
        },
        isUserProfilePublic() {
            return this.userContext?.user?.hasPublicProfile;
        },
        isImpersonated() {
            return this.userContext?.isImpersonated;
        },
        hasCreatorPermission() {
            const creatorPermissions = this.userContext?.user?.creatorPermissions || {};
            return Object.keys(creatorPermissions).length > 0;
        },
        nickname() {
            return this.userContext?.user?.nickname;
        },
        canFollowUsers() {
            return this.isAuthenticated && this.isUserProfilePublic && !!this.nickname;
        },
        hasUserQuirk() {
            return (quirk) => ((this.userContext?.user?.quirkFlags ?? 0) & quirk) !== 0;
        },
        hasPushNotificationsEnabled() {
            return this.userContext?.user?.hasPushNotificationsEnabled;
        },
        userId() {
            return this.userContext?.user?.userID;
        },
        hasRetailerAccess() {
            return this.userContext?.user?.hasRetailerAccess;
        },
    },
});